/**
 * useAnalysisResult Hook - Fetch Analysis Results from Supabase
 *
 * Fetches analysis results by ID from the analysis_history table.
 * Supports both organic flow (from React Query cache) and shared links (server fetch).
 *
 * Flow:
 * 1. Check React Query cache first (instant for recent analyses)
 * 2. Shared link token → get-shared-analysis Edge Function
 * 3. Direct access → get-analysis Edge Function (server-side decryption proxy)
 * 4. Transform database format to DramaAnalysisResponse type
 *
 * M2.2 UPDATE (2026-02-08): Replaced direct RLS queries with get-analysis
 * Edge Function proxy. Messages may be AES-256-GCM encrypted at rest and
 * require server-side decryption (MESSAGE_ENCRYPTION_KEY is a secret).
 *
 * @param analysisId - UUID of the analysis to fetch
 * @returns { data, isLoading, error }
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  decodeObjectValues,
  authenticatedFetch,
  fetchJsonValidated,
  toNarrativeClarityAnalysisEnvelope,
} from "@dramadetector/api-client";
import type {
  DramaAnalysisResponse,
  MessageContext,
  NarrativeClarityAnalysisEnvelope,
} from "@dramadetector/shared";
import { SharedAnalysisDto } from "@dramadetector/shared/schemas";
import { useAnalysisStore } from "../store/analysis";
import { useAuth } from "../contexts/AuthContext";
import { captureEvent } from "../lib/analytics";
import { getEdgeFunctionUrl } from "../lib/api-url";

/** Tier-specific TTL hours for expiration analytics (hoisted to module scope) */
const TIER_TTL_HOURS: Record<string, number> = {
  anonymous: 2, free: 24, drama_plus: 180 * 24, dramax: 365 * 24,
};

/**
 * Envelope-backed consumer result with minimal compatibility aliases.
 *
 * NarrativeClarity envelope is the primary consumer contract.
 * `legacyAnalysis` is the bounded compatibility adapter for older results
 * components that have not migrated yet.
 */
export interface AnalysisResultWithMessage extends NarrativeClarityAnalysisEnvelope {
  /** Compatibility alias for existing route/components that still key by id */
  id: string;
  /** Original message text (required for inline annotations) */
  message?: string;
  /** Message context (work, dating, family, friends) */
  context?: MessageContext;
  /** TTL expiration timestamp — used for staleness indicator on results page */
  ttlExpiresAt?: string;
  /** Bounded compatibility adapter for legacy result consumers */
  legacyAnalysis: DramaAnalysisResponse;
}

interface UseAnalysisResultReturn {
  data: AnalysisResultWithMessage | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Transform database analysis_history row to AnalysisResultWithMessage
 *
 * Includes `message` and `context` fields needed for tier-gated features:
 * - Inline annotations (require original message text)
 * - Context-specific episode titles (NarratorEpisodeCard)
 *
 * UPDATE (2026-01-23): Fixed tier-gated content not showing because
 * `message` was not being passed to ModernResultsFull component.
 *
 * UPDATE (2026-01-30): Added HTML entity decoding for database fetches.
 * Messages are HTML-escaped during storage (XSS prevention) but need
 * decoding when retrieved from database to display properly.
 */
/**
 * Safely parse a field that may be a JSON string or already an array.
 * Returns [] for null, undefined, or unparseable values.
 */
function ensureArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** B-lite shape for raw rewrite rows from DB (fields may use DB naming or API naming) */
interface RawRewriteRow {
  original?: string;
  improved?: string;
  rewritten?: string;
  reason?: string;
  reasoning?: string;
  improvementAreas?: unknown[];
}

/** Shape of a row from the analysis_history table (or Edge Function response) */
interface AnalysisHistoryRow {
  id: string;
  message?: string;
  context?: MessageContext;
  ttl_expires_at?: string;
  score?: number | null;
  summary?: string;
  narrator_intro?: string;
  category?: string | null;
  issues?: unknown;
  rewrites?: unknown;
  patterns?: unknown;
  transformations?: unknown;
  ui_spec?: unknown;
  contentManifest?: unknown;
  provider?: string;
  model?: string;
  latency_ms?: number;
  tokens_used?: number;
  cost_usd?: number;
  cached?: boolean;
  safety?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toSafetyInfo(value: unknown): DramaAnalysisResponse['safety'] | undefined {
  return isRecord(value) ? (value as unknown as DramaAnalysisResponse['safety']) : undefined;
}

function transformAnalysisHistoryToLegacyResponse(analysis: AnalysisHistoryRow): DramaAnalysisResponse & {
  message?: string;
  context?: MessageContext;
  ttlExpiresAt?: string;
} {
  // analysis_history stores fields directly (not in JSONB wrapper)
  // FIX (2026-03-20): Fields like rewrites/issues may arrive as JSON strings
  // from Redis cache or text DB columns. ensureArray() safely parses them.
  const rawResponse = {
    id: analysis.id,
    // Original message and context (required for inline annotations)
    message: analysis.message,
    context: analysis.context,
    // TTL expiration (staleness indicator on results page)
    ttlExpiresAt: analysis.ttl_expires_at || undefined,
    // Core analysis fields
    score: analysis.score ?? null,
    summary: analysis.summary || "",
    narrator_intro: analysis.narrator_intro || undefined,
    category: analysis.category ?? null,
    issues: ensureArray(analysis.issues),
    // Normalize database field names back to DramaAnalysisResponse format
    // Database stores: { original, improved, reason }
    // Frontend expects: { original, rewritten, reasoning, improvementAreas }
    rewrites: (ensureArray(analysis.rewrites) as RawRewriteRow[]).map((r) => ({
      original: r.original,
      rewritten: r.improved || r.rewritten || '',
      reasoning: r.reason || r.reasoning || '',
      improvementAreas: r.improvementAreas || [],
    })),
    // Tier-gated features (Layer 2+)
    // ensureArray: these may arrive as JSON strings from Redis cache or text DB columns
    patterns: ensureArray(analysis.patterns),
    transformations: ensureArray(analysis.transformations),
    // P6.2: Stored content manifest (eliminates LLM call on revisits)
    contentManifest: analysis.ui_spec || analysis.contentManifest || undefined,
    // Safety / crisis metadata
    safety: toSafetyInfo(analysis.safety),
    // Provider metadata
    provider: analysis.provider || "unknown",
    model: analysis.model || "unknown",
    metadata: {
      provider: analysis.provider || "unknown",
      model: analysis.model || "unknown",
      latencyMs: analysis.latency_ms || undefined,
      tokensUsed: analysis.tokens_used || undefined,
      costUsd: analysis.cost_usd || undefined,
      cached: analysis.cached || false,
      timestamp: new Date().toISOString(), // Required by AnalysisMetadata type
    },
  };

  // Decode HTML entities in all string values (fixes &#039; display issue)
  // This matches the decoding applied to fresh API responses in normalizeAnalysisResponse
  return decodeObjectValues(rawResponse) as DramaAnalysisResponse & {
    message?: string;
    context?: MessageContext;
    ttlExpiresAt?: string;
  };
}

function transformAnalysisHistoryToEnvelope(analysis: AnalysisHistoryRow): AnalysisResultWithMessage {
  const legacy = transformAnalysisHistoryToLegacyResponse(analysis);
  const envelope = toNarrativeClarityAnalysisEnvelope(
    {
      message: legacy.message || '',
      context: legacy.context,
    },
    legacy,
    {
      policy: {
        policyKey: 'analysis.history-hydration',
        policyVersion: 'legacy-history-hydration',
        decisionMode: 'advisory',
      },
      provenance: {
        schemaVersion: 'drama-analysis-response-v1',
        canonicalizationApplied: ['analysis-history-hydration'],
        fallbackApplied: !('result' in legacy),
      },
    }
  );

  const safety = toSafetyInfo(analysis.safety);
  const hasScoredResult = typeof analysis.score === 'number' && typeof analysis.category === 'string';

  return {
    ...envelope,
    id: legacy.id,
    message: legacy.message,
    context: legacy.context,
    ttlExpiresAt: legacy.ttlExpiresAt,
    legacyAnalysis: legacy,
    result: hasScoredResult ? envelope.result : null,
    safety,
  };
}

/**
 * Fetch analysis result by ID from Supabase
 *
 * Uses React Query for caching and automatic background refetching.
 * Checks cache first before making network request.
 *
 * @param analysisId - UUID of the analysis (optional for token-based access)
 * @param token - Shared link token for public access (bypasses analysisId requirement)
 */
export function useAnalysisResult(analysisId?: string, token?: string): UseAnalysisResultReturn {
  const queryClient = useQueryClient();
  // Phase 2 (NocoDB #3870): use `isRealUser` for analytics tier classification
  // so anon BA users (who HAVE an accessToken but user.isAnonymous=true) report
  // as 'anonymous', not 'free', in PostHog events.
  const { accessToken, isRealUser } = useAuth();
  // Get matching Zustand store entry for this analysisId (organic flow)
  // NOTE: Selector targets a single item instead of the full history array
  // to avoid re-renders when unrelated history entries change.
  const storeEntry = useAnalysisStore((state) =>
    analysisId ? state.history.find((h) => h.result?.id === analysisId) : undefined
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ["analysis-result", analysisId, token],
    // Gate the query until either (a) we have an `accessToken` (BA Bearer for
    // authed + anon BA flows) or (b) we have a shared-link `token` (skipAuth path).
    // Without this gate, the queryFn races AuthContext bootstrap on cold load:
    // status starts at 'anonymous' (default), authenticatedFetch's pre-gate only
    // awaits on 'pending', so the request goes out with no Authorization header
    // and the worker returns 401. Sentry DRAMA-EDGE-17 / NocoDB #4100 — visible
    // only after the post-#4101 Sentry pipeline restoration.
    enabled: !!accessToken || !!token,
    queryFn: async () => {
      // ============================================================================
      // CRITICAL FIX: Check React Query cache FIRST
      // ============================================================================
      // If analysis exists in cache (organic flow from useAnalyze mutation),
      // return it immediately WITHOUT hitting the database.
      // This prevents RLS errors for anonymous users whose analyses aren't stored in DB.
      //
      // Flow:
      // 1. User analyzes → useAnalyze mutation stores in cache key "analyze-drama"
      // 2. Navigate to /results/[id] → useAnalysisResult checks cache first
      // 3. Cache hit → return immediately (NO database fetch, NO RLS error)
      // 4. Cache miss (shared link/refresh) → fetch from DB (authenticated users only)

      const cachedData = queryClient.getQueryData<{ data: DramaAnalysisResponse }>([
        "analyze-drama",
      ]);

      // Check if cached analysis matches the requested ID
      // MEDIUM-3 FIX (2026-01-29): Check both flat and nested ID structure
      // Unified pipeline may return { data: { result: { id: ... } } }
      const responseId = cachedData?.data?.id || (cachedData?.data as DramaAnalysisResponse & { result?: { id?: string } })?.result?.id;
      if (analysisId && responseId === analysisId) {
        // Use pre-selected Zustand store entry (already filtered by analysisId in selector)
        const storeMessage = storeEntry?.message;
        const storeContext = storeEntry?.context;

        // FIX (2026-01-23): Unified pipeline now includes message/context directly in response
        // Prefer API response values over Zustand (handles both unified and legacy flows)
        if (!cachedData) return null;
        const apiResponse = {
          ...(cachedData.data as DramaAnalysisResponse & {
            message?: string;
            context?: MessageContext;
            ttlExpiresAt?: string;
          }),
          id: responseId,
        };
        const message = apiResponse.message || storeMessage || '';
        const context = apiResponse.context || storeContext;
        const envelope = toNarrativeClarityAnalysisEnvelope(
          {
            message,
            context,
          },
          apiResponse,
          {
            policy: {
              policyKey: 'analysis.cache-hit',
              policyVersion: 'react-query-cache',
              decisionMode: 'advisory',
            },
            provenance: {
              schemaVersion: 'drama-analysis-response-v1',
              canonicalizationApplied: ['react-query-cache'],
              fallbackApplied: !('result' in apiResponse),
            },
          }
        );

        console.log("[useAnalysisResult] Cache hit - using envelope-backed analysis", {
          analysisId: analysisId?.substring(0, 8) + "..." || 'token-based',
          source: "react-query-cache",
          hasApiMessage: !!apiResponse.message,
          hasStoreMessage: !!storeMessage,
          hasMessage: !!message,
          hasContext: !!context,
        });

        return {
          ...envelope,
          id: responseId,
          message,
          context,
          ttlExpiresAt: apiResponse.ttlExpiresAt,
          legacyAnalysis: apiResponse,
        } as AnalysisResultWithMessage;
      }

      // ============================================================================
      // Cache miss: Fetch from database (shared link or page refresh)
      // ============================================================================
      console.log("[useAnalysisResult] Cache miss - fetching from database", {
        analysisId: analysisId?.substring(0, 8) + "..." || 'token-based',
        source: token ? "shared-link-edge" : "supabase-db",
        hasToken: !!token,
      });

      // ============================================================================
      // Shared link flow: Call Edge Function with token
      // ============================================================================
      // If a shared link token is provided, bypass direct RLS query and fetch
      // via the get-shared-analysis Edge Function (service role, no auth required).
      //
      // Wire shape is validated end-to-end via `SharedAnalysisDto` from
      // `@dramadetector/shared/schemas/content` — schema drift surfaces as
      // an immediate runtime error instead of silently breaking the shared
      // view (Wave 4C DTO migration).
      if (token) {
        const edgeUrl = `${getEdgeFunctionUrl('get-shared-analysis')}?token=${encodeURIComponent(token)}`;
        console.log("[useAnalysisResult] Fetching via shared link edge function", {
          token: token.substring(0, 8) + "...",
          hasToken: true,
        });

        try {
          const sharedDto = await fetchJsonValidated(SharedAnalysisDto, edgeUrl, {
            method: "GET",
            skipAuth: true,
          });

          console.log("[useAnalysisResult] Shared link edge function success", {
            token: token.substring(0, 8) + "...",
            hasData: true,
          });

          return transformAnalysisHistoryToEnvelope(sharedDto as unknown as AnalysisHistoryRow);
        } catch (err) {
          // fetchJsonValidated throws a generic Error for non-2xx responses
          // with the server's `error` field as the message. Re-map known
          // cases to the friendly messages the shared-link UI expects.
          const message = err instanceof Error ? err.message : String(err);
          console.log("[useAnalysisResult] Shared link edge function error", {
            token: token.substring(0, 8) + "...",
            error: message,
          });
          const isNotFound =
            message.toLowerCase().includes("not found") ||
            message.toLowerCase().includes("expired") ||
            message.toLowerCase().includes("invalid");
          throw new Error(
            isNotFound
              ? "Shared link not found or has expired."
              : `Failed to fetch shared analysis: ${message}`,
          );
        }
      }

      // ============================================================================
      // Server-side proxy: Fetch via get-analysis Edge Function
      // ============================================================================
      // M2.2: Messages may be AES-256-GCM encrypted at rest. The client cannot
      // decrypt because MESSAGE_ENCRYPTION_KEY is a server-side secret.
      // The get-analysis Edge Function handles decryption before returning data.
      if (!analysisId) {
        throw new Error("analysisId is required for direct access (non-token flow)");
      }

      console.log("[useAnalysisResult] Fetching via Edge Function", {
        analysisId: analysisId.substring(0, 8) + "...",
        isAuthenticated: isRealUser,
      });

      const edgeUrl = `${getEdgeFunctionUrl('get-analysis')}?id=${encodeURIComponent(analysisId)}`;
      const response = await authenticatedFetch(edgeUrl, { method: "GET" });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({ error: response.statusText }));

        if (response.status === 404) {
          // Track expiration event for tier-aware monitoring.
          // Phase 2: `isRealUser` is correct here — anon BA users report as
          // 'anonymous' (not 'free') in analytics.
          const isAuthenticated = isRealUser;
          const visitorTier = isAuthenticated ? 'free' : 'anonymous';
          captureEvent("Analysis_Expired", {
            analysisId: analysisId.substring(0, 8),
            isAuthenticated,
            creatorTier: visitorTier,
            visitorTier,
            source: token ? 'shared_link' : 'organic',
            hoursOldEstimate: 0,
            tierTTLHours: TIER_TTL_HOURS[visitorTier],
          });

          const message = isAuthenticated
            ? "Analysis not found. It may have been deleted or has expired. Try re-running the analysis."
            : "This is a private link. Please ask the owner to share via the 'Share via Link' button for public access.";

          console.log("[useAnalysisResult] Analysis not found", {
            analysisId: analysisId.substring(0, 8) + "...",
            isAuthenticated,
          });
          throw new Error(message);
        }

        // Enhanced error logging with cache context
        console.error("[useAnalysisResult] Edge Function error", {
          analysisId: analysisId.substring(0, 8) + "...",
          status: response.status,
          error: errorBody.error,
          endpoint: token ? 'get-shared-analysis' : 'get-analysis',
        });

        // Provide actionable error messages
        const errorMessage = response.status === 500
          ? "Server error while fetching analysis. This may be due to encrypted data issues. Please try refreshing the page."
          : `Failed to fetch analysis: ${errorBody.error || response.statusText}`;

        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (!result?.data) {
        throw new Error("Invalid response from get-analysis service");
      }

      // Transform database format to API response format
      return transformAnalysisHistoryToEnvelope(result.data);
    },
    // Cache for 5 minutes (analyses rarely change after creation)
    staleTime: 5 * 60 * 1000,
    // Keep in cache for 10 minutes even when unused
    gcTime: 10 * 60 * 1000,
    // Don't retry on auth/not-found errors — these won't resolve on retry
    retry: (failureCount, error) => {
      const msg = error?.message || "";
      if (
        msg.includes("not found") ||
        msg.includes("expired") ||
        msg.includes("access") ||
        msg.includes("Cannot coerce") ||
        msg.includes("Unauthorized")
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });

  return {
    data: data || null,
    isLoading,
    error: error as Error | null,
  };
}
